// SAOKO catalog runtime — extracted from sections/saoko-whatsapp-catalog.liquid
// (spec: docs/plans/2026-07-11-007-externalize-catalog-js.md). The section owns
// SSR + configuration: everything this script needs arrives via data-sawac-*
// attributes and the [data-sawac-icons] JSON block inside each [data-sawac-root].
// Init is idempotent per root (dataset.sawacInitialized), late sections re-init
// via shopify:section:load, and duplicate script tags are harmless by design.
(() => {
  const initCatalogRoot = (root) => {
    if (!root || root.dataset.sawacInitialized === 'true') return;
    root.dataset.sawacInitialized = 'true';

      const state = {
        activeFilter: 'all',
        activeHandles: [],
        cart: null,
        pendingQtySyncs: new Map(),
        confirmedQuantities: new Map()
      };

      const selectors = {
        card: '[data-sawac-card]',
        chip: '[data-sawac-filter]',
        quickAdd: '[data-sawac-quick-add]',
        tierAdd: '[data-sawac-tier-add]',
        qtyMinus: '[data-sawac-qty-minus]',
        remove: '[data-sawac-remove]',
        openSheet: '[data-sawac-open-sheet]',
        openImage: '[data-sawac-open-image]',
        sheetClose: '[data-sawac-sheet-close]',
        lightboxClose: '[data-sawac-lightbox-close]'
      };

      const rootUrl = (() => {
        const fromShopify = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
        const value = fromShopify || root.dataset.rootUrl || '/';
        return value.endsWith('/') ? value : `${value}/`;
      })();

      const searchInput = root.querySelector('[data-sawac-search]');
      const noResults = root.querySelector('[data-sawac-no-results]');
      const inlineStatus = root.querySelector('[data-sawac-inline-status]');
      const progressiveRevealRearms = [];
      let queryUrlTimer = null;

      const whatsappNumber = String(root.dataset.whatsappNumber || '').replace(/[^\d]/g, '');
      const sheet = {
        dialog: root.querySelector('[data-sawac-sheet]'),
        media: root.querySelector('[data-sawac-sheet-media]'),
        sku: root.querySelector('[data-sawac-sheet-sku]'),
        title: root.querySelector('[data-sawac-sheet-title]'),
        tiers: root.querySelector('[data-sawac-sheet-tiers]'),
        productLink: root.querySelector('[data-sawac-sheet-product-link]')
      };

      const lightbox = {
        dialog: root.querySelector('[data-sawac-lightbox]'),
        track: null,
        dots: null,
        slides: [],
        count: 0,
        index: 0,
        scrollFrame: 0,
        // Last viewed image per product (session only), keyed by first base URL.
        memory: {},
        memoryKey: null
      };

      // Native showModal() does not stop the page behind a <dialog> from
      // scrolling on mobile — touch-dragging inside the sheet/lightbox chains
      // through to the body once the panel hits its scroll edge. Lock the body
      // (position: fixed, preserving the scroll offset) while any sawac dialog
      // is open and restore it on close. Ref-counted so an overlapping
      // sheet+lightbox does not unlock early.
      const scrollLock = (() => {
        let locks = 0;
        let savedY = 0;
        return {
          lock() {
            if (locks === 0) {
              savedY = window.scrollY || window.pageYOffset || 0;
              const body = document.body;
              body.style.position = 'fixed';
              body.style.top = '-' + savedY + 'px';
              body.style.left = '0';
              body.style.right = '0';
              body.style.width = '100%';
              body.style.overflow = 'hidden';
            }
            locks += 1;
          },
          unlock() {
            if (locks === 0) return;
            locks -= 1;
            if (locks > 0) return;
            const body = document.body;
            body.style.position = '';
            body.style.top = '';
            body.style.left = '';
            body.style.right = '';
            body.style.width = '';
            body.style.overflow = '';
            // Restore synchronously — the two-arg scrollTo would inherit the
            // global html { scroll-behavior: smooth }, animating the page back
            // from the top and re-showing the background movement the lock
            // exists to prevent. 'instant' forces an immediate jump.
            window.scrollTo({ top: savedY, left: 0, behavior: 'instant' });
          }
        };
      })();
      const serverSearch = {
        group: root.querySelector('[data-sawac-server-search-group]'),
        grid: root.querySelector('[data-sawac-server-search-grid]'),
        title: root.querySelector('[data-sawac-server-search-title]'),
        timer: null,
        abort: null,
        seq: 0,
        query: '',
        loading: false,
        hasResults: false
      };
      if (lightbox.dialog) {
        // Track + dots built here (not in markup) so theme check does not flag the
        // empty runtime <img> sources; slides are (re)built per product on open.
        lightbox.track = document.createElement('div');
        lightbox.track.className = 'sawac-lightbox-track sawac-peek-track';
        lightbox.track.setAttribute('data-sawac-lightbox-track', '');
        lightbox.dialog.appendChild(lightbox.track);

        lightbox.dots = document.createElement('div');
        lightbox.dots.className = 'sawac-lightbox-dots';
        lightbox.dots.setAttribute('data-sawac-lightbox-dots', '');
        lightbox.dots.hidden = true;
        lightbox.dialog.appendChild(lightbox.dots);

        // Sync the active dot as the buyer swipes (rAF-throttled).
        lightbox.track.addEventListener('scroll', () => {
          window.cancelAnimationFrame(lightbox.scrollFrame);
          lightbox.scrollFrame = window.requestAnimationFrame(updateLightboxFromScroll);
        }, { passive: true });
      }

      function normalize(value) {
        return String(value || '')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .trim();
      }

      function parseHandles(value) {
        return String(value || '')
          .split(',')
          .map((handle) => normalize(handle))
          .filter(Boolean);
      }

      function setInlineStatus(message, isError = false) {
        if (!inlineStatus) return;
        inlineStatus.textContent = message || '';
        inlineStatus.classList.toggle('is-error', Boolean(isError));
        if (message) window.setTimeout(() => {
          if (inlineStatus.textContent === message) inlineStatus.textContent = '';
        }, 2400);
      }

      function syncActiveButtons() {
        root.querySelectorAll(selectors.chip).forEach((button) => {
          const isActive = button.dataset.sawacFilter === state.activeFilter;
          button.classList.toggle('is-active', isActive);
          if (button.classList.contains('sawac-chip')) {
            button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          }
        });
      }

      // Catalog model. Each collection group ships its products as a JSON array in a
      // [data-sawac-group-products] json script (built by saoko-catalog-card in
      // index_only mode). Parse once: catalogById (id -> entry, for the sheet) and
      // and groupModels (per-group ordered entries + the grid to render them into). The
      // heavy card markup is NOT in the initial document — renderCard() builds it on
      // demand (scroll reveal / filter), so first paint no longer waits on ~680 cards.
      const catalogById = new Map();
      const groupModels = [];
      const groupModelByEl = new Map();
      root.querySelectorAll('[data-sawac-group]').forEach((section) => {
        const grid = section.querySelector('[data-sawac-grid]');
        const holder = section.querySelector('[data-sawac-group-products]');
        const entries = registerCatalogEntries(holder);
        const previewAttr = parseInt(section.getAttribute('data-sawac-eager-count'), 10);
        const deferred = section.hasAttribute('data-sawac-deferred-group');
        const shell = section.hasAttribute('data-sawac-shell-group');
        const gm = {
          section,
          grid,
          entries,
          previewCount: Number.isNaN(previewAttr) ? entries.length : previewAttr,
          deferred,
          progressive: section.hasAttribute('data-sawac-progressive'),
          shell,
          shellGroup: shell,
          fragmentUrl: section.getAttribute('data-fragment-url') || '',
          fullFragmentUrl: section.getAttribute('data-full-fragment-url') || '',
          // Only a deferred (preview-capped) or shell group has more products to fetch;
          // eager groups already ship their full product_limit index inline.
          fullyHydrated: !deferred,
          fullHydrating: null,
          hydrating: null
        };
        // Progressive (collection) groups grow this on scroll; static groups keep previewCount.
        gm.revealCount = gm.previewCount;
        // Tag the server-rendered eager cards with their entry ordinal so on-demand
        // inserts land in catalog order regardless of render history.
        if (grid) {
          entries.forEach((entry, i) => {
            if (!entry || entry.id == null) return;
            const card = grid.querySelector('[data-product-id="' + entry.id + '"]');
            if (card) card.dataset.ord = String(i);
          });
        }
        groupModels.push(gm);
        groupModelByEl.set(section, gm);
      });

      // Register a [data-sawac-group-products] index payload into catalogById so the
      // pricing sheet / lightbox / readCardData resolve for those products. Used at init
      // for each catalog group, and again when server-search injects results (those
      // products are off the local catalog, so their entries aren't in catalogById yet).
      // Returns the parsed entries (or []).
      function registerCatalogEntries(scriptEl) {
        if (!scriptEl) return [];
        let entries = [];
        try { entries = JSON.parse(scriptEl.textContent) || []; } catch (error) { entries = []; }
        entries.forEach((entry) => {
          if (entry && entry.id != null) catalogById.set(String(entry.id), entry);
        });
        return entries;
      }

      function installGroupIndexScript(gm, entries) {
        if (!gm || !gm.section) return;
        const script = document.createElement('script');
        script.type = 'application/json';
        script.setAttribute('data-sawac-group-products', '');
        script.textContent = JSON.stringify(entries || []).replace(/<\//g, '<\\/');
        const existing = gm.section.querySelector('[data-sawac-group-products]');
        if (existing) existing.remove();
        if (gm.grid && gm.grid.parentNode) {
          gm.grid.parentNode.insertBefore(script, gm.grid);
        } else {
          gm.section.appendChild(script);
        }
      }

      // Swap a Shopify CDN image URL to another width (image_url always appends width=N),
      // so one stored URL yields the 240px thumb + 1200px lightbox the card needs.
      function imageAt(url, width) {
        if (!url) return '';
        if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + width);
        return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
      }

      function cardMediaHtml(entry) {
        const title = escapeHtml(entry.title);
        if (entry.image) {
          // Image alt mirrors the Liquid card: custom featured_image.alt, else the
          // product title. The aria-label stays on the product title (matches the
          // server "Ampliar imagen de {title}"), only the alt/data-alt carry imageAlt.
          const imgAlt = escapeHtml(entry.imageAlt || entry.title);
          const large = escapeHtml(imageAt(entry.image, 1200));
          const src = escapeHtml(imageAt(entry.image, 240));
          const srcset = escapeHtml(
            imageAt(entry.image, 120) + ' 120w, ' + imageAt(entry.image, 180) + ' 180w, ' + imageAt(entry.image, 240) + ' 240w'
          );
          // Full image set for the swipeable lightbox gallery — mirrors the server card's
          // data-sawac-images (a JSON array of 420px base URLs). Falls back to the single
          // featured image so older index entries (no "images") still open one slide.
          const imageList = (Array.isArray(entry.images) && entry.images.length) ? entry.images : [entry.image];
          const imagesAttr = escapeHtml(JSON.stringify(imageList));
          return (
            '<button class="sawac-card-link sawac-media" type="button" data-sawac-open-image data-sawac-images="' + imagesAttr + '" data-sawac-image-large="' + large + '" data-sawac-image-alt="' + imgAlt + '" aria-label="Ampliar imagen de ' + title + '">' +
              '<img class="sawac-card__image" src="' + src + '" srcset="' + srcset + '" sizes="70px" loading="lazy" alt="' + imgAlt + '">' +
            '</button>'
          );
        }
        return (
          '<button class="sawac-card-link sawac-media" type="button" data-sawac-open-sheet aria-label="Ver opciones de ' + title + '">' +
            '<span class="sawac-placeholder" aria-hidden="true">' + escapeHtml(entry.initial || '') + '</span>' +
          '</button>'
        );
      }

      function cardCopyHtml(entry) {
        const hasTier = Boolean(entry.bigbag || entry.fardo);
        const priceRow = hasTier
          ? ''
          : '<span class="sawac-price-row"><span class="sawac-price">' + escapeHtml(entry.base.priceFormatted) + '</span></span>';
        return (
          '<span class="sawac-card-copy" data-sawac-open-sheet>' +
            '<span class="sawac-sku">' + escapeHtml(entry.sku) + '</span>' +
            '<button class="sawac-card-link sawac-product-title" type="button" data-sawac-open-sheet>' + escapeHtml(entry.title) + '</button>' +
            priceRow +
          '</span>'
        );
      }

      // The card's volume price band — mirrors the tier band in saoko-catalog-card.liquid.
      // Every cell is a one-tap add-to-cart button (delegated data-sawac-tier-add handler).
      function cardTierBandHtml(entry) {
        const hasBigbag = Boolean(entry.bigbag);
        const hasFardo = Boolean(entry.fardo);
        if (!hasBigbag && !hasFardo) return '';
        const vid = escapeHtml(entry.base.id);
        const basePrice = entry.base.priceFormatted;
        const tier1End = (hasBigbag ? Number(entry.bigbag.doz) : Number(entry.fardo.doz)) - 1;

        let cells =
          '<button type="button" class="sawac-tier-cell" data-sawac-tier-add data-variant-id="' + vid + '" data-tier-doz="1" data-tier-min="1" data-tier-price="' + escapeHtml(basePrice) + '" data-tier-price-cents="' + escapeHtml(entry.base.price) + '" aria-label="Agregar 1 docena — ' + escapeHtml(basePrice) + '">' +
            '<span class="sawac-tier-range">1' + (tier1End > 1 ? '–' + tier1End : '') + ' doz</span>' +
            '<span class="sawac-tier-price">' + escapeHtml(basePrice) + '</span>' +
            '<span class="sawac-tier-plus" aria-hidden="true">+1</span>' +
          '</button>';

        if (hasBigbag) {
          const bd = Number(entry.bigbag.doz);
          const bigbagCents = Number(entry.bigbag.pricePerDoz) * 100;
          const bigbagPrice = entry.bigbag.priceFormatted;
          const rangeEnd = hasFardo ? '–' + (Number(entry.fardo.doz) - 1) : '+';
          cells +=
            '<button type="button" class="sawac-tier-cell" data-sawac-tier-add data-variant-id="' + vid + '" data-tier-doz="' + bd + '" data-tier-min="' + bd + '" data-tier-price="' + escapeHtml(bigbagPrice) + '" data-tier-price-cents="' + escapeHtml(bigbagCents) + '" aria-label="Agregar ' + bd + ' docenas — ' + escapeHtml(bigbagPrice) + ' por docena">' +
              '<span class="sawac-tier-range">' + bd + rangeEnd + ' doz</span>' +
              '<span class="sawac-tier-price">' + escapeHtml(bigbagPrice) + '</span>' +
              '<span class="sawac-tier-plus" aria-hidden="true">+' + bd + '</span>' +
            '</button>';
        }

        if (hasFardo) {
          const fd = Number(entry.fardo.doz);
          const fardoCents = Number(entry.fardo.pricePerDoz) * 100;
          const fardoPrice = entry.fardo.priceFormatted;
          cells +=
            '<button type="button" class="sawac-tier-cell" data-sawac-tier-add data-variant-id="' + vid + '" data-tier-doz="' + fd + '" data-tier-min="' + fd + '" data-tier-price="' + escapeHtml(fardoPrice) + '" data-tier-price-cents="' + escapeHtml(fardoCents) + '" aria-label="Agregar 1 fardo (' + fd + ' docenas) — ' + escapeHtml(fardoPrice) + ' por docena">' +
              '<span class="sawac-tier-range">' + fd + '+ doz</span>' +
              '<span class="sawac-tier-price">' + escapeHtml(fardoPrice) + '</span>' +
              '<span class="sawac-tier-plus" aria-hidden="true">+fardo</span>' +
            '</button>';
        }

        return (
          '<div class="sawac-tier-band" data-sawac-tier-band data-variant-id="' + vid + '" aria-label="Precios por volumen">' +
            cells +
          '</div>' +
          '<p class="sawac-tier-now" data-sawac-tier-now data-variant-id="' + vid + '" hidden></p>'
        );
      }

      // Build one <article> from an index entry, at markup parity with
      // snippets/saoko-catalog-card.liquid (same classes + data-* hooks so the delegated
      // click handlers, quantity sync, filter, sheet, and lightbox all keep working).
      // The qty stepper reuses tierQtyControl() — identical to the snippet's row markup.
      function renderCard(entry) {
        const article = document.createElement('article');
        article.className = 'sawac-card';
        article.setAttribute('data-sawac-card', '');
        article.setAttribute('data-product-id', String(entry.id));
        article.innerHTML =
          '<div class="sawac-card-main">' + cardMediaHtml(entry) + cardCopyHtml(entry) + '</div>' +
          tierQtyControl(entry.base.id, entry.title) +
          cardTierBandHtml(entry);
        return article;
      }

      function getCard(gm, i) {
        const entry = gm.entries[i];
        return entry && gm.grid ? gm.grid.querySelector('[data-product-id="' + entry.id + '"]') : null;
      }

      // Render entry i into its group's grid if absent, inserting in catalog order
      // (before the first already-rendered card with a higher ordinal). Idempotent.
      function ensureCardRendered(gm, i) {
        const entry = gm.entries[i];
        if (!entry || !gm.grid) return null;
        const existing = getCard(gm, i);
        if (existing) return existing;
        const card = renderCard(entry);
        card.dataset.ord = String(i);
        const siblings = gm.grid.children;
        let inserted = false;
        for (let s = 0; s < siblings.length; s += 1) {
          const ord = parseInt(siblings[s].dataset.ord, 10);
          if (!Number.isNaN(ord) && ord > i) { gm.grid.insertBefore(card, siblings[s]); inserted = true; break; }
        }
        if (!inserted) gm.grid.appendChild(card);
        return card;
      }

      // A below-the-fold group server-renders zero cards. Revealing it renders its first
      // previewCount cards from the index (matching an eager group's initial view); the
      // deeper cards render on filter/search. Idempotent. Returns true if any card was
      // rendered, so the caller can re-sync cart quantities onto the new rows.
      function revealDeferredGroup(groupEl) {
        const gm = groupModelByEl.get(groupEl);
        if (!gm || gm.shell || gm.section.classList.contains('is-revealed')) return false;
        gm.section.classList.add('is-revealed');
        const count = Math.min(gm.previewCount, gm.entries.length);
        let renderedAny = false;
        for (let i = 0; i < count; i += 1) {
          let card = getCard(gm, i);
          if (!card) { card = ensureCardRendered(gm, i); renderedAny = true; }
          // Un-hide preview cards on reveal. A card rendered by an earlier filter that
          // was then cleared is left hidden=true (the unrevealed group showed reserved
          // space, not cards); on reveal it must become visible. Without this, a
          // filter→clear→scroll flow leaves the revealed group missing its first rows
          // (settleReveal only re-runs applyFilters while still filtered).
          if (card) card.hidden = false;
        }
        return renderedAny;
      }

      function syncGroupHydrationBusy(gm) {
        if (!gm || !gm.section) return;
        if (gm.hydrating || gm.fullHydrating) {
          gm.section.setAttribute('aria-busy', 'true');
        } else {
          gm.section.removeAttribute('aria-busy');
        }
      }

      function hydrateShellGroup(groupEl) {
        const gm = groupModelByEl.get(groupEl);
        if (!gm || !gm.shell) return Promise.resolve(false);
        if (gm.hydrating) return gm.hydrating;
        if (!gm.fragmentUrl) return Promise.resolve(false);

        gm.hydrating = fetch(gm.fragmentUrl, {
          headers: { 'Accept': 'text/html' },
          credentials: 'same-origin'
        })
          .then((response) => {
            if (!response.ok) throw new Error('No se pudo cargar la categoría.');
            return response.text();
          })
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const holder = doc.querySelector('[data-sawac-group-products]');
            if (!holder) throw new Error('La categoría no entregó índice.');
            // A scroll preview and an explicit full-category request can overlap. Once
            // the full request has won, this smaller response must never replace it.
            // Returning true still lets the observer reveal the now-ready full group.
            if (gm.fullyHydrated) return true;
            const entries = registerCatalogEntries(holder);
            gm.entries = entries;
            gm.revealCount = gm.previewCount;
            gm.shell = false;
            installGroupIndexScript(gm, entries);
            gm.section.removeAttribute('data-sawac-shell-group');
            return true;
          })
          .catch(() => false)
          .finally(() => {
            gm.hydrating = null;
            syncGroupHydrationBusy(gm);
          });
        syncGroupHydrationBusy(gm);
        return gm.hydrating;
      }

      function revealDeferredGroupWhenReady(groupEl) {
        const gm = groupModelByEl.get(groupEl);
        if (!gm) return Promise.resolve({ revealedAny: false, hydratedAny: false });
        if (gm.shell) {
          return hydrateShellGroup(groupEl).then((hydrated) => {
            if (!hydrated) return { revealedAny: false, hydratedAny: false };
            return { revealedAny: true, hydratedAny: revealDeferredGroup(groupEl) };
          });
        }
        return Promise.resolve({ revealedAny: true, hydratedAny: revealDeferredGroup(groupEl) });
      }

      // A chip tap or ?categoria= deep-link is explicit user intent to see the WHOLE
      // category, not just the preview-sized index a deferred/shell group ships (or
      // scroll-approach fetches). Fetches the group's full-size fragment (product_limit
      // products) and replaces its entries in place — previously-rendered preview cards
      // (ordinals 0..previewCount-1) still line up with the same products since both
      // fragments iterate the same collection in the same order, so only the new,
      // higher-ordinal entries need rendering (applyFilters/ensureCardRendered do that).
      // Idempotent: gm.fullyHydrated short-circuits repeat chip taps on the same group.
      function hydrateFullGroup(groupEl) {
        const gm = groupModelByEl.get(groupEl);
        if (!gm) return Promise.resolve(false);
        if (gm.fullyHydrated) return Promise.resolve(false);
        if (gm.fullHydrating) return gm.fullHydrating;
        const url = gm.fullFragmentUrl || gm.fragmentUrl;
        if (!url) return Promise.resolve(false);

        gm.fullHydrating = fetch(url, {
          headers: { 'Accept': 'text/html' },
          credentials: 'same-origin'
        })
          .then((response) => {
            if (!response.ok) throw new Error('No se pudo cargar la categoría.');
            return response.text();
          })
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const holder = doc.querySelector('[data-sawac-group-products]');
            if (!holder) throw new Error('La categoría no entregó índice.');
            const entries = registerCatalogEntries(holder);
            // The full fragment is authoritative even if products changed between the
            // initial HTML and this response. A pending preview is prevented above from
            // overwriting this result when it eventually completes.
            gm.entries = entries;
            installGroupIndexScript(gm, entries);
            gm.fullyHydrated = true;
            gm.shell = false;
            gm.section.removeAttribute('data-sawac-shell-group');
            return true;
          })
          .catch(() => false)
          .finally(() => {
            gm.fullHydrating = null;
            syncGroupHydrationBusy(gm);
          });
        syncGroupHydrationBusy(gm);
        return gm.fullHydrating;
      }

      // A chip usually maps to one or two collections (its own homepage group), but some
      // map to a whole metaobject GROUP bundling many collections at once (e.g. a broad
      // demographic tag spanning a dozen product-type groups). Fully hydrating every one
      // of those in a single tap would re-fetch full indexes for as many groups as the
      // original 57-group homepage — exactly the payload the shell/deferred split exists
      // to avoid. Cap it to the first FULL_HYDRATE_GROUP_CAP matching groups (document
      // order, i.e. the ones the user reaches first); groups beyond the cap keep their
      // own "Más modelos" link as the way to see the rest of that sub-collection.
      const FULL_HYDRATE_GROUP_CAP = 6;

      function hydrateGroupsForActiveFilter() {
        groupModels.forEach((gm) => {
          if (!gm.section.hasAttribute('data-sawac-capped-group')) return;
          gm.section.removeAttribute('data-sawac-capped-group');
          if (gm.grid) gm.grid.style.removeProperty('display');
        });
        if (state.activeFilter === 'all' || !state.activeHandles.length) return;

        // Build the document-order window before excluding groups which were hydrated
        // earlier. Otherwise each repeat tap advances to another six groups and quietly
        // defeats the cap.
        const matching = groupModels.filter((gm) => {
            const handle = normalize(gm.section.getAttribute('data-group-handle') || '');
            return state.activeHandles.includes(handle);
          });
        const hydrationWindow = matching.slice(0, FULL_HYDRATE_GROUP_CAP);
        const targets = hydrationWindow.filter((gm) => !gm.fullyHydrated);

        // Shells beyond the stable request window remain useful navigation rather than
        // disappearing as empty filtered groups. Collapse their reserved grid so only
        // the heading and "Más modelos" fallback link remain.
        matching.slice(FULL_HYDRATE_GROUP_CAP).forEach((gm) => {
          if (gm.shellGroup && !gm.fullyHydrated) {
            gm.section.setAttribute('data-sawac-capped-group', '');
            if (gm.grid) gm.grid.style.display = 'none';
          }
        });

        if (!targets.length) return;
        Promise.all(targets.map((gm) => hydrateFullGroup(gm.section))).then((results) => {
          if (!results.some(Boolean)) return;
          applyFilters();
          syncRowQuantities(state.cart || { item_count: 0, total_price: 0, currency: 'CLP', items: [] });
          reapplyPendingQuantitySyncs();
        });
      }

      function serverSearchAvailable() {
        return Boolean(serverSearch.group && serverSearch.grid);
      }

      function updateNoResults(localVisibleCount) {
        if (!noResults) return;
        const serverVisible = serverSearchAvailable() && !serverSearch.group.hidden && serverSearch.hasResults;
        noResults.hidden = localVisibleCount !== 0 || serverSearch.loading || serverVisible;
      }

      function clearServerSearch() {
        if (!serverSearchAvailable()) return;
        if (serverSearch.timer) window.clearTimeout(serverSearch.timer);
        serverSearch.timer = null;
        if (serverSearch.abort) serverSearch.abort.abort();
        serverSearch.abort = null;
        serverSearch.seq += 1;
        serverSearch.query = '';
        serverSearch.loading = false;
        serverSearch.hasResults = false;
        serverSearch.grid.innerHTML = '';
        serverSearch.group.hidden = true;
      }

      function cardMatchesActiveCategory(card) {
        if (state.activeFilter === 'all') return true;
        // Categories live in the JSON index (registered into catalogById), not on the
        // card — visible cards carry only data-product-id. Resolve via readCardData so
        // server-search cards (off the local catalog, registered on inject) match the
        // active chip correctly. Without this they all read '' and hide under any chip.
        const data = readCardData(card);
        const categories = normalize(data ? data.categories : '');
        return state.activeHandles.some((handle) => categories.includes(`,${handle},`));
      }

      function hasVisibleLocalCatalogCard(productId) {
        if (productId == null) return false;
        const selector = '[data-product-id="' + CSS.escape(String(productId)) + '"]';
        return groupModels.some((gm) => {
          if (!gm.grid || gm.section.hidden) return false;
          const card = gm.grid.querySelector(selector);
          return Boolean(card && !card.hidden && card.getClientRects().length > 0);
        });
      }

      function markServerSearchCards() {
        if (!serverSearchAvailable()) return 0;
        let visibleCount = 0;
        serverSearch.grid.querySelectorAll(selectors.card).forEach((card) => {
          card.setAttribute('data-sawac-server-card', '');
          const isDuplicate = hasVisibleLocalCatalogCard(card.getAttribute('data-product-id'));
          const shouldShow = !isDuplicate && cardMatchesActiveCategory(card);
          card.hidden = !shouldShow;
          if (shouldShow) visibleCount += 1;
        });
        return visibleCount;
      }

      function syncServerSearchRows() {
        syncRowQuantities(state.cart || { item_count: 0, total_price: 0, currency: 'CLP', items: [] });
        reapplyPendingQuantitySyncs();
      }

      // Re-run duplicate marking after late local renders. A server response that
      // lands while the filtered-render tail is still queued dedupes against an
      // incomplete DOM (hasVisibleLocalCatalogCard sees only rendered cards), so a
      // queued product would otherwise show twice — in its group AND in the
      // server-search grid. Only over-visibility needs fixing: a hidden group has
      // no visible server cards, so there is nothing to dedupe.
      function refreshServerSearchDedupe() {
        if (!serverSearchAvailable() || serverSearch.group.hidden) return;
        const visibleCount = markServerSearchCards();
        serverSearch.hasResults = visibleCount > 0;
        serverSearch.group.hidden = visibleCount === 0;
      }

      // A keystroke supersedes any pending or in-flight server search immediately;
      // the debounced applyFilters() reschedules for the current value. Without
      // this, the previous query's 180ms timer can fire (or its in-flight response
      // land) mid-debounce and inject stale "Resultados de búsqueda" under a newer
      // input value. The seq bump orphans the stale run's handlers wholesale; the
      // next schedule/run/clear rebuilds serverSearch state from scratch.
      function supersedeServerSearch() {
        if (serverSearch.timer) { window.clearTimeout(serverSearch.timer); serverSearch.timer = null; }
        if (serverSearch.abort) { serverSearch.abort.abort(); serverSearch.abort = null; }
        serverSearch.seq += 1;
      }

      function runServerSearch(query, localVisibleCount = 0) {
        if (!serverSearchAvailable()) return;
        const trimmed = String(query || '').trim();
        if (trimmed.length < 2) {
          clearServerSearch();
          updateNoResults(localVisibleCount);
          return;
        }

        if (serverSearch.abort) serverSearch.abort.abort();
        const controller = new AbortController();
        const seq = serverSearch.seq + 1;
        serverSearch.seq = seq;
        serverSearch.abort = controller;
        serverSearch.query = trimmed;
        serverSearch.loading = true;
        serverSearch.hasResults = false;
        serverSearch.group.hidden = true;
        if (serverSearch.title) serverSearch.title.textContent = `Resultados de búsqueda: ${trimmed}`;
        updateNoResults(localVisibleCount);

        // Shopify storefront search matches whole words; the local index matches
        // substrings. A trailing * makes the last term a prefix query so partial
        // words / partial SKUs ("bufan", "buf10") still reach deferred-tail products.
        const serverQuery = trimmed.endsWith('*') ? trimmed : `${trimmed}*`;
        const url = `${rootUrl}search?type=product&q=${encodeURIComponent(serverQuery)}&view=sawac-cards`;
        fetch(url, {
          headers: { 'Accept': 'text/html' },
          credentials: 'same-origin',
          signal: controller.signal
        })
          .then((response) => {
            if (!response.ok) throw new Error('No se pudo buscar en todos los productos.');
            return response.text();
          })
          .then((html) => {
            if (serverSearch.seq !== seq) return;
            serverSearch.grid.innerHTML = html;
            // Server-search products are off the local catalog, so register their index
            // entries into catalogById first — otherwise readCardData() can't resolve a
            // result card and its pricing sheet / lightbox would silently fail to open.
            registerCatalogEntries(serverSearch.grid.querySelector('[data-sawac-group-products]'));
            const visibleCount = markServerSearchCards();
            const totalCount = serverSearch.grid.querySelectorAll(selectors.card).length;
            serverSearch.hasResults = visibleCount > 0;
            serverSearch.group.hidden = visibleCount === 0;
            if (totalCount > 0) syncServerSearchRows();
          })
          .catch((error) => {
            if (error && error.name === 'AbortError') return;
            if (serverSearch.seq !== seq) return;
            serverSearch.grid.innerHTML = '';
            serverSearch.hasResults = false;
            serverSearch.group.hidden = true;
          })
          .finally(() => {
            if (serverSearch.seq !== seq) return;
            serverSearch.loading = false;
            serverSearch.abort = null;
            updateNoResults(localVisibleCount);
          });
      }

      function scheduleServerSearch(query, localVisibleCount) {
        if (!serverSearchAvailable()) return;
        if (serverSearch.timer) window.clearTimeout(serverSearch.timer);
        serverSearch.timer = null;
        const trimmed = String(query || '').trim();
        if (trimmed.length < 2) {
          clearServerSearch();
          updateNoResults(localVisibleCount);
          return;
        }
        serverSearch.loading = true;
        serverSearch.hasResults = false;
        serverSearch.group.hidden = true;
        updateNoResults(localVisibleCount);
        serverSearch.timer = window.setTimeout(() => runServerSearch(trimmed, localVisibleCount), 180);
      }

      // Filtered-view render chunking: the index scan and the first
      // FILTER_RENDER_SYNC_BUDGET missing cards render synchronously (document
      // order — the top of the page fills first), the tail lands in frame-sized
      // batches. A broad match (1-char query, wide chip) used to render every
      // missing match inside the keystroke/tap task — a main homepage INP
      // driver. Batched cards are below the sync-rendered ones, so deferring
      // them shifts nothing above the fold. A newer applyFilters() run bumps
      // the generation and orphans any queued tail (it re-decides everything).
      const FILTER_RENDER_SYNC_BUDGET = 24;
      const FILTER_RENDER_BATCH = 12;
      let filterRenderGeneration = 0;

      function drainFilterRenderQueue(queue, generation) {
        if (!queue.length) return;
        const step = () => {
          if (generation !== filterRenderGeneration) return;
          let rendered = false;
          for (let n = 0; queue.length && n < FILTER_RENDER_BATCH; n += 1) {
            const pending = queue.shift();
            if (!getCard(pending[0], pending[1])) {
              ensureCardRendered(pending[0], pending[1]);
              rendered = true;
            }
          }
          if (rendered) {
            // Same post-render row sync as applyFilters: rows born in this batch
            // weren't present at the last cart sync.
            syncRowQuantities(state.cart || { item_count: 0, total_price: 0, currency: 'CLP', items: [] });
            reapplyPendingQuantitySyncs();
            refreshServerSearchDedupe();
          }
          if (queue.length) window.requestAnimationFrame(step);
        };
        window.requestAnimationFrame(step);
      }

      function applyFilters() {
        const query = normalize(searchInput ? searchInput.value : '');
        const isFiltered = state.activeFilter !== 'all' || Boolean(query);
        const wasFiltered = root.classList.contains('is-filtered');
        root.classList.toggle('is-filtered', isFiltered);
        const becameUnfiltered = wasFiltered && !isFiltered;
        let visibleCount = 0;
        let renderedAny = false;
        let syncRenderBudget = FILTER_RENDER_SYNC_BUDGET;
        const renderQueue = [];
        filterRenderGeneration += 1;
        const renderGeneration = filterRenderGeneration;

        groupModels.forEach((gm) => {
          const previewLimit = Math.min(gm.revealCount, gm.entries.length);
          const revealed = !gm.deferred || gm.section.classList.contains('is-revealed');
          let groupVisible = 0;

          gm.entries.forEach((entry, i) => {
            const categories = normalize(entry.categories || '');
            const searchText = normalize(entry.search || '');
            const matchesCategory = state.activeFilter === 'all' || state.activeHandles.some((handle) => categories.includes(`,${handle},`));
            const matchesSearch = !query || searchText.includes(query);
            // Filtered: show every match (render on demand). Unfiltered: show only the
            // preview set, and only once the group is revealed — an unrevealed deferred
            // group stays empty (reserved space) until the scroll observer fills it.
            const shouldShow = isFiltered
              ? (matchesCategory && matchesSearch)
              : (i < previewLimit && revealed);

            let card = getCard(gm, i);
            if (shouldShow && !card) {
              if (syncRenderBudget > 0) {
                card = ensureCardRendered(gm, i);
                syncRenderBudget -= 1;
                renderedAny = true;
              } else {
                // Rendered a frame later by drainFilterRenderQueue; counts as
                // visible now so no-results / server-search see the real total.
                renderQueue.push([gm, i]);
              }
            }
            if (card) card.hidden = !shouldShow;
            if (shouldShow) { groupVisible += 1; visibleCount += 1; }
          });

          // Keep an unrevealed deferred group visible (header + reserved space) when
          // unfiltered so the observer can still fill it in; otherwise hide empty groups.
          if (gm.section.hasAttribute('data-sawac-capped-group')) {
            gm.section.hidden = false;
          } else if (!isFiltered && gm.deferred && !revealed) {
            gm.section.hidden = false;
          } else {
            gm.section.hidden = groupVisible === 0;
          }
        });

        // Local (index) search done; always hand off 2+ char queries to server-search
        // because deferred groups carry only a preview-sized local index.
        scheduleServerSearch(query, visibleCount);
        updateNoResults(visibleCount);
        // Rows just rendered weren't present at the last cart sync; reflect the
        // current cart (and any pending optimistic changes) on them now.
        if (renderedAny) {
          syncRowQuantities(state.cart || { item_count: 0, total_price: 0, currency: 'CLP', items: [] });
          reapplyPendingQuantitySyncs();
        }
        drainFilterRenderQueue(renderQueue, renderGeneration);
        syncActiveButtons();
        if (becameUnfiltered) progressiveRevealRearms.forEach((rearm) => rearm());
      }

      function setFilter(button) {
        const filterId = button.dataset.sawacFilter || 'all';
        state.activeFilter = filterId;
        state.activeHandles = filterId === 'all' ? [] : parseHandles(button.dataset.sawacHandles);
        hydrateGroupsForActiveFilter();
        applyFilters();
        writeFilterToUrl(filterId);
      }

      // Reflect the active category chip in the URL (?categoria=<group-handle>) so a
      // filtered catalog view can be copied from the address bar and shared (e.g. over
      // WhatsApp); opening that link preselects the chip on load. "all" clears the param.
      // replaceState (not pushState) keeps one history entry per page so the back button
      // isn't littered with a step per chip tap. Other params (e.g. ?q=) are preserved.
      function writeFilterToUrl(filterId) {
        if (!window.history || !window.history.replaceState) return;
        let url;
        try { url = new URL(window.location.href); } catch (error) { return; }
        if (filterId && filterId !== 'all') {
          url.searchParams.set('categoria', filterId);
        } else {
          url.searchParams.delete('categoria');
        }
        window.history.replaceState(window.history.state, '', url);
      }

      // Reflect the search box in the URL (?q=<query>) so merchants can copy the
      // filtered catalog link from the address bar. Other params (e.g. ?categoria=)
      // are preserved, and an empty query clears only ?q.
      function writeQueryToUrl(rawQuery) {
        if (!window.history || !window.history.replaceState) return;
        let url;
        try { url = new URL(window.location.href); } catch (error) { return; }
        const query = String(rawQuery || '').trim();
        if (query) {
          url.searchParams.set('q', query);
        } else {
          url.searchParams.delete('q');
        }
        window.history.replaceState(window.history.state, '', url);
      }

      function scheduleQueryUrlWrite(rawQuery) {
        if (queryUrlTimer) window.clearTimeout(queryUrlTimer);
        queryUrlTimer = window.setTimeout(() => {
          queryUrlTimer = null;
          writeQueryToUrl(rawQuery);
        }, 200);
      }

      async function fetchCart(options = {}) {
        // Shopify serves /cart.js without a Cache-Control header, so it is
        // heuristically cacheable. WebKit (iOS Safari and the WhatsApp in-app
        // browser this catalog is shared into) caches it aggressively and
        // replays a stale, often empty, cart on the re-read that follows every
        // add/change; Blink/Chrome does not, so the bug is mobile-only. That
        // stale read makes syncRowQuantities() reset the stepper back to "+",
        // which reads as "the quantity won't stick". Force a fresh read: the
        // no-store option plus a cache-busting param, since Safari has ignored
        // no-store in the past but can never match a unique URL.
        const response = await fetch(`${rootUrl}cart.js?t=${Date.now()}`, {
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store'
        });
        if (!response.ok) throw new Error('No se pudo leer el carrito.');
        const cart = await response.json();
        state.cart = cart;
        updateConfirmedQuantities(cart);
        syncRowQuantities(cart);
        syncThemeCart(cart, options);
        reapplyPendingQuantitySyncs();
        return cart;
      }

      function variantQuantityMap(cart) {
        const map = new Map();
        if (!cart || !Array.isArray(cart.items)) return map;
        cart.items.forEach((item) => {
          const id = String(item.variant_id || item.id || '');
          if (!id) return;
          map.set(id, (map.get(id) || 0) + Number(item.quantity || 0));
        });
        return map;
      }

      function getVariantQuantity(variantId) {
        return variantQuantityMap(state.cart).get(String(variantId)) || 0;
      }

      // The quantity the UI should treat as current for a click. A variant with a
      // pending sync has its authoritative in-flight target on the pending entry;
      // state.cart can be transiently clobbered when another variant's flush replaces
      // it with a server cart that predates this variant's optimistic change.
      function getEffectiveVariantQuantity(variantId) {
        const id = String(variantId);
        const pending = state.pendingQtySyncs.get(id);
        if (pending) return Math.max(0, Number(pending.targetQty || 0));
        return getVariantQuantity(id);
      }

      function updateConfirmedQuantities(cart) {
        state.confirmedQuantities = variantQuantityMap(cart);
      }

      function getConfirmedVariantQuantity(variantId) {
        return state.confirmedQuantities.get(String(variantId)) || 0;
      }

      function setConfirmedVariantQuantity(variantId, quantity) {
        state.confirmedQuantities.set(String(variantId), Math.max(0, Number(quantity || 0)));
      }

      function reapplyPendingQuantitySyncs() {
        state.pendingQtySyncs.forEach((entry, id) => {
          applyOptimisticVariantQuantity(id, Math.max(0, Number(entry.targetQty || 0)));
        });
      }

      // Reflect the buyer's current position in the volume price band: highlight
      // the tier their quantity reaches and show "Llevas N doz · $X / doz" under
      // it, so the card answers "which price am I getting and how many do I have".
      function updateTierBand(variantId, qty) {
        const id = CSS.escape(String(variantId));
        root.querySelectorAll(`[data-sawac-tier-band][data-variant-id="${id}"]`).forEach((band) => {
          let activeCell = null;
          band.querySelectorAll('[data-tier-min]').forEach((cell) => {
            const min = Number(cell.dataset.tierMin) || 0;
            const isActive = qty > 0 && qty >= min;
            if (isActive) activeCell = cell; // cells are in ascending order; last wins
            cell.classList.remove('is-current');
          });
          if (activeCell) activeCell.classList.add('is-current');

          const now = band.parentElement
            ? band.parentElement.querySelector(`[data-sawac-tier-now][data-variant-id="${id}"]`)
            : null;
          if (now) {
            if (activeCell) {
              // "$2.000 / doz   $170.000" — the unit price actually being paid
              // and the line total. The quantity itself already lives in the
              // stepper above, so it is not repeated here.
              const priceCents = Number(activeCell.dataset.tierPriceCents) || 0;
              const totalClp = Math.round((priceCents * qty) / 100);
              now.textContent = '';
              const unit = document.createElement('span');
              unit.className = 'sawac-tier-now-unit';
              unit.textContent = `${activeCell.dataset.tierPrice || ''} / doz`;
              const total = document.createElement('span');
              total.className = 'sawac-tier-now-total';
              total.textContent = `$${totalClp.toLocaleString('es-CL')}`;
              now.append(unit, total);
              now.hidden = false;
            } else {
              now.textContent = '';
              now.hidden = true;
            }
          }
        });
      }

      function updateVariantRows(variantId, qty) {
        root.querySelectorAll(`[data-sawac-row-actions][data-variant-id="${CSS.escape(String(variantId))}"]`).forEach((wrap) => {
          const qtyWrap = wrap.querySelector('[data-sawac-row-qty-wrap]');
          const qtyText = wrap.querySelector('[data-sawac-row-qty]');
          const minusButton = wrap.querySelector('[data-sawac-qty-minus]');
          const removeButton = wrap.querySelector('[data-sawac-remove]');
          if (qtyText) qtyText.value = String(qty);
          if (minusButton) minusButton.disabled = qty <= 0;
          if (removeButton) removeButton.hidden = qty <= 0;
          if (qtyWrap) {
            qtyWrap.classList.toggle('is-empty', qty <= 0);
            qtyWrap.classList.toggle('is-syncing', state.pendingQtySyncs.has(String(variantId)));
          }
        });
        updateTierBand(variantId, qty);
      }

      function syncRowQuantities(cart) {
        const quantities = variantQuantityMap(cart);
        root.querySelectorAll('[data-sawac-row-actions]').forEach((wrap) => {
          const variantId = String(wrap.dataset.variantId || '');
          const qty = quantities.get(variantId) || 0;
          const qtyWrap = wrap.querySelector('[data-sawac-row-qty-wrap]');
          const qtyText = wrap.querySelector('[data-sawac-row-qty]');
          const minusButton = wrap.querySelector('[data-sawac-qty-minus]');
          const removeButton = wrap.querySelector('[data-sawac-remove]');
          if (qtyText) qtyText.value = String(qty);
          if (minusButton) minusButton.disabled = qty <= 0;
          if (removeButton) removeButton.hidden = qty <= 0;
          if (qtyWrap) {
            qtyWrap.classList.toggle('is-empty', qty <= 0);
            qtyWrap.classList.toggle('is-syncing', state.pendingQtySyncs.has(variantId));
          }
          updateTierBand(variantId, qty);
        });
      }

      function syncThemeCart(cart, options = {}) {
        const count = Number(cart && cart.item_count || 0);
        document.querySelectorAll('[data-cart-count], [data-cart-count-bubble], .cart-count-bubble span[aria-hidden="true"]').forEach((el) => {
          el.textContent = String(count);
        });
        sessionStorage.setItem('cart-count', JSON.stringify({ value: String(count), timestamp: Date.now() }));
        if (options.silentEvent) return;
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            data: {
              itemCount: count,
              source: 'saoko-catalog',
              sections: options.sections
            }
          }
        }));
      }

      function ensureLocalCart() {
        if (state.cart && Array.isArray(state.cart.items)) return state.cart;
        state.cart = { item_count: 0, total_price: 0, currency: 'CLP', items: [] };
        return state.cart;
      }

      function applyOptimisticVariantQuantity(variantId, nextQty) {
        const cart = ensureLocalCart();
        const id = String(variantId);
        const previousQty = getVariantQuantity(id);
        const delta = nextQty - previousQty;
        let item = cart.items.find((line) => String(line.variant_id || line.id || '') === id);
        if (!item && nextQty > 0) {
          item = { id: Number(id), variant_id: Number(id), quantity: 0 };
          cart.items.push(item);
        }
        if (item) item.quantity = Math.max(0, nextQty);
        cart.items = cart.items.filter((line) => Number(line.quantity || 0) > 0);
        cart.item_count = Math.max(0, Number(cart.item_count || 0) + delta);
        updateVariantRows(id, Math.max(0, nextQty));
        syncThemeCart(cart, { silentEvent: true });
        return previousQty;
      }

      async function postAdd(variantId, quantity = 1, properties = null) {
        const item = { id: Number(variantId), quantity };
        if (properties) item.properties = properties;
        const response = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items: [item] })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.description || payload.message || 'No se pudo agregar al carrito.');
        return payload;
      }

      async function postChange(variantId, quantity) {
        const response = await fetch(`${rootUrl}cart/change.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ id: String(variantId), quantity })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.description || payload.message || 'No se pudo actualizar el carrito.');
        state.cart = payload;
        updateConfirmedQuantities(payload);
        return payload;
      }

      function scheduleVariantQuantitySync(variantId) {
        const id = String(variantId || '');
        if (!id) return;

        const existing = state.pendingQtySyncs.get(id);
        if (existing) {
          existing.targetQty = getVariantQuantity(id);
          if (!existing.flushing) {
            window.clearTimeout(existing.timer);
            existing.timer = window.setTimeout(() => flushVariantQuantitySync(id), 260);
          }
          updateVariantRows(id, existing.targetQty);
          return;
        }

        const entry = {
          baseQty: getConfirmedVariantQuantity(id),
          targetQty: getVariantQuantity(id),
          flushing: false,
          timer: window.setTimeout(() => flushVariantQuantitySync(id), 260)
        };
        state.pendingQtySyncs.set(id, entry);
        updateVariantRows(id, entry.targetQty);
      }

      async function flushVariantQuantitySync(variantId) {
        const id = String(variantId || '');
        const entry = state.pendingQtySyncs.get(id);
        if (!entry) return;

        const targetQty = Math.max(0, Number(entry.targetQty || 0));
        const baseQty = Math.max(0, Number(entry.baseQty || 0));
        if (targetQty === baseQty) {
          state.pendingQtySyncs.delete(id);
          updateVariantRows(id, targetQty);
          return;
        }

        entry.flushing = true;
        try {
          let cart = null;
          if (baseQty === 0 && targetQty > 0) {
            await postAdd(id, targetQty, null);
            setConfirmedVariantQuantity(id, targetQty);
            cart = await fetchCart({ silentEvent: true });
          } else {
            cart = await postChange(id, targetQty);
          }

          if (entry.targetQty !== targetQty) {
            entry.baseQty = getConfirmedVariantQuantity(id);
            entry.flushing = false;
            entry.timer = window.setTimeout(() => flushVariantQuantitySync(id), 80);
            applyOptimisticVariantQuantity(id, entry.targetQty);
            return;
          }

          state.pendingQtySyncs.delete(id);
          if (cart) {
            state.cart = cart;
            syncRowQuantities(cart);
            // The server cart only reflects variants confirmed so far. Other variants
            // still in pendingQtySyncs were just overwritten by their stale server value
            // (or dropped from state.cart). Re-apply their optimistic targets so their
            // rows and state.cart quantities are not reverted by this flush.
            reapplyPendingQuantitySyncs();
            syncThemeCart(cart);
          }
          updateVariantRows(id, targetQty);
          setInlineStatus(targetQty > baseQty ? 'Agregado' : 'Actualizado');
        } catch (error) {
          state.pendingQtySyncs.delete(id);
          applyOptimisticVariantQuantity(id, getConfirmedVariantQuantity(id));
          updateVariantRows(id, getConfirmedVariantQuantity(id));
          setInlineStatus(error.message || 'No se pudo actualizar.', true);
        }
      }

      // Adds `n` units to a variant in one shot, reusing the optimistic + debounced
      // sync path so the change merges into the same cart line as manual +/- taps.
      function quickAddN(variantId, n) {
        const id = String(variantId || '');
        const step = Math.max(1, Number(n) || 1);
        if (!id) return;
        const nextQty = getEffectiveVariantQuantity(id) + step;
        applyOptimisticVariantQuantity(id, nextQty);
        setInlineStatus('Agregado');
        scheduleVariantQuantitySync(id);
      }

      function quickAdd(button) {
        quickAddN(button.dataset.variantId, 1);
      }

      function minusOne(button) {
        const variantId = String(button.dataset.variantId || '');
        if (!variantId) return;
        const currentQty = getEffectiveVariantQuantity(variantId);
        if (currentQty <= 0) return;
        const nextQty = currentQty - 1;
        applyOptimisticVariantQuantity(variantId, nextQty);
        setInlineStatus('Actualizado');
        scheduleVariantQuantitySync(variantId);
      }

      function clearVariant(id) {
        const variantId = String(id || '');
        if (!variantId || getEffectiveVariantQuantity(variantId) <= 0) return;
        applyOptimisticVariantQuantity(variantId, 0);
        setInlineStatus('Actualizado');
        scheduleVariantQuantitySync(variantId);
      }

      function escapeHtml(value) {
        return String(value == null ? '' : value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }

      function readCardData(card) {
        if (!card) return null;
        const id = card.getAttribute('data-product-id');
        if (id == null) return null;
        return catalogById.get(String(id)) || null;
      }

      // Stepper/remove icons arrive via the section's [data-sawac-icons] JSON block —
      // Liquid inline_asset_content can't run inside an external asset.
      const sawacIcons = (() => {
        try { return JSON.parse(root.querySelector('[data-sawac-icons]')?.textContent || '{}') || {}; } catch { return {}; }
      })();
      const iconMinusSvg = sawacIcons.minus || '';
      const iconPlusSvg = sawacIcons.plus || '';
      const iconDeleteSvg = sawacIcons.delete || '';

      // Builds one tier row reusing the same qty-control markup as the card rows.
      // syncRowQuantities()/updateVariantRows() query [data-sawac-row-actions] by variant id
      // across the whole root, so the sheet row stays in sync with the matching card row.
      function tierQtyControl(variantId, label) {
        const id = escapeHtml(variantId);
        const safeLabel = escapeHtml(label);
        return (
          '<div class="sawac-row-actions" data-sawac-row-actions data-variant-id="' + id + '">' +
            '<button class="sawac-row-remove" type="button" data-sawac-remove data-variant-id="' + id + '" aria-label="Quitar ' + safeLabel + '" hidden>' + iconDeleteSvg + '</button>' +
            '<div class="quantity-selector cart-primary-typography sawac-row-qty is-empty" data-sawac-row-qty-wrap data-variant-id="' + id + '">' +
              '<button class="button quantity-minus button-unstyled" type="button" data-sawac-qty-minus data-variant-id="' + id + '" aria-label="Restar ' + safeLabel + '" disabled>' +
                '<span class="svg-wrapper icon-plus">' + iconMinusSvg + '</span>' +
              '</button>' +
              '<input class="sawac-row-qty-count" data-sawac-row-qty type="number" value="0" min="0" inputmode="numeric" aria-label="Cantidad de ' + safeLabel + '" readonly>' +
              '<button class="button quantity-plus button-unstyled" type="button" data-sawac-quick-add data-variant-id="' + id + '" aria-label="Agregar ' + safeLabel + '">' +
                '<span class="svg-wrapper icon-plus">' + iconPlusSvg + '</span>' +
              '</button>' +
            '</div>' +
          '</div>'
        );
      }

      function tierRowHtml(label, priceText, subText, controlHtml) {
        return (
          '<div class="sawac-tier">' +
            '<div class="sawac-tier-info">' +
              '<p class="sawac-tier-label">' + escapeHtml(label) + '</p>' +
              '<p class="sawac-tier-price">' + escapeHtml(priceText) + '</p>' +
              (subText ? '<p class="sawac-tier-sub">' + escapeHtml(subText) + '</p>' : '') +
            '</div>' +
            controlHtml +
          '</div>'
        );
      }

      // A wholesale tier row with a one-tap "Agregar N doz" button. The button adds N
      // docenas of the single (base) variant; the Shopify Function discounts the whole
      // line to the tier price once that variant's total reaches the threshold N.
      // variantId is always the base variant — every tier funnels into the same cart line.
      function tierAddRowHtml(variantId, n, label, priceText, buttonLabel) {
        const note = 'Descuento automático al llevar ' + n + '+ doz';
        const button =
          '<button class="sawac-tier-consult" type="button" data-sawac-tier-add' +
            ' data-variant-id="' + escapeHtml(variantId) + '"' +
            ' data-tier-doz="' + escapeHtml(n) + '">' +
            escapeHtml(buttonLabel) +
          '</button>';
        return (
          '<div class="sawac-tier">' +
            '<div class="sawac-tier-info">' +
              '<p class="sawac-tier-label">' + escapeHtml(label) + '</p>' +
              '<p class="sawac-tier-price">' + escapeHtml(priceText) + '</p>' +
              '<p class="sawac-tier-sub">' + escapeHtml(note) + '</p>' +
            '</div>' +
            button +
          '</div>'
        );
      }

      function bigbagRowHtml(data) {
        const n = Number(data.bigbag.doz) || 0;
        return tierAddRowHtml(
          data.base.id,
          n,
          'Por mayor (' + n + '+ doz)',
          data.bigbag.priceFormatted + ' / docena',
          'Agregar ' + n + ' doz'
        );
      }

      function fardoRowHtml(data) {
        const n = Number(data.fardo.doz) || 0;
        return tierAddRowHtml(
          data.base.id,
          n,
          'Por fardo (' + n + ' doz)',
          data.fardo.priceFormatted + ' / docena',
          'Agregar fardo'
        );
      }

      function consultRowHtml(data) {
        let consult;
        if (whatsappNumber) {
          const text = 'Hola, quiero consultar el precio por fardo de ' + data.title + ' (' + data.sku + ').';
          const href = 'https://wa.me/' + whatsappNumber + '?text=' + encodeURIComponent(text);
          consult = '<a class="sawac-tier-consult" href="' + escapeHtml(href) + '" target="_blank" rel="noopener">Consultar por WhatsApp</a>';
        } else {
          consult = '<span class="sawac-tier-consult">Consultar por WhatsApp</span>';
        }
        return (
          '<div class="sawac-tier">' +
            '<div class="sawac-tier-info">' +
              '<p class="sawac-tier-label">Por fardo</p>' +
              '<p class="sawac-tier-sub">Stock por fardo a pedido</p>' +
            '</div>' +
            consult +
          '</div>'
        );
      }

      function renderSheet(data) {
        if (data.image) {
          sheet.media.innerHTML = '<img src="' + escapeHtml(data.image) + '" alt="' + escapeHtml(data.title) + '" width="64" height="64">';
        } else {
          sheet.media.textContent = data.initial || '';
        }
        sheet.sku.textContent = data.sku || '';
        sheet.title.textContent = data.title || '';

        const base = data.base || data.mayor; // back-compat with older card JSON
        let html = '';
        if (base && base.id) {
          const hasWholesale = Boolean(data.bigbag || data.fardo);
          html += tierRowHtml(
            'Por docena',
            base.priceFormatted + ' / docena',
            hasWholesale ? null : 'Venta por docena',
            tierQtyControl(base.id, data.title + ' por docena')
          );
        }
        if (data.bigbag && base && base.id) {
          // tier builders read data.base; normalize so back-compat data.mayor also works.
          html += bigbagRowHtml({ ...data, base });
        }
        if (data.fardo && base && base.id) {
          html += fardoRowHtml({ ...data, base });
        } else {
          html += consultRowHtml(data);
        }
        sheet.tiers.innerHTML = html;

        if (data.url) {
          sheet.productLink.href = data.url;
          sheet.productLink.hidden = false;
        } else {
          sheet.productLink.hidden = true;
        }

        // Reflect current cart quantities into the freshly injected tier rows.
        // Reapply pending syncs so a tier opened mid-flight shows the optimistic
        // target (matching the card row), not a transiently clobbered cart value.
        if (state.cart) {
          syncRowQuantities(state.cart);
          reapplyPendingQuantitySyncs();
        }
      }

      function openSheet(card) {
        if (!sheet.dialog) return;
        const data = readCardData(card);
        if (!data) return;
        renderSheet(data);
        if (typeof sheet.dialog.showModal === 'function') {
          sheet.dialog.showModal();
        } else {
          sheet.dialog.setAttribute('open', '');
        }
        scrollLock.lock();
      }

      function closeSheet() {
        if (!sheet.dialog || !sheet.dialog.open) return;
        sheet.dialog.classList.add('is-closing');
        const finish = () => {
          sheet.dialog.classList.remove('is-closing');
          sheet.dialog.close();
          scrollLock.unlock();
        };
        const onEnd = () => {
          sheet.dialog.removeEventListener('animationend', onEnd);
          finish();
        };
        sheet.dialog.addEventListener('animationend', onEnd);
        // Fallback if the animation does not fire (reduced motion, etc.).
        window.setTimeout(() => {
          if (sheet.dialog.classList.contains('is-closing')) {
            sheet.dialog.removeEventListener('animationend', onEnd);
            finish();
          }
        }, 320);
      }

      if (sheet.dialog) {
        // Native dialog cancel (ESC) — animate out instead of instant close.
        sheet.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          closeSheet();
        });
        // Backdrop click: the dialog element fills the backdrop, the panel is inside it.
        sheet.dialog.addEventListener('click', (event) => {
          if (event.target === sheet.dialog) closeSheet();
        });
      }

      // Warm the full-res image cache without competing with the critical page load.
      // Intent preloads (hover/touch) stay immediate for the tapped image; background
      // preloads start only after window.load, run at low priority, one at a time,
      // and pause briefly whenever the buyer scrolls/touches/types.
      const loadedLarge = new Set();
      const inFlightLarge = new Map();
      const backgroundPreloadQueue = [];
      const backgroundPreloadLimit = 18;
      const backgroundPreloadInitialDelay = 1600;
      const backgroundPreloadBetweenDelay = 900;
      let backgroundPreloadTimer = 0;
      let backgroundPreloadInFlight = false;
      let lastInteractionAt = Date.now();

      function markBuyerInteraction() {
        lastInteractionAt = Date.now();
      }

      ['scroll', 'wheel', 'touchstart', 'pointerdown', 'click', 'keydown', 'input', 'focusin'].forEach((eventName) => {
        window.addEventListener(eventName, markBuyerInteraction, { passive: true });
      });

      function preloadLarge(button, options = {}) {
        const src = button.getAttribute('data-sawac-image-large');
        const priority = options.priority || 'low';
        if (!src || loadedLarge.has(src)) return false;
        const currentPriority = inFlightLarge.get(src);
        if (currentPriority === 'high') return false;
        if (currentPriority === 'low' && priority !== 'high') return false;
        inFlightLarge.set(src, priority);
        const warm = new Image();
        let settled = false;
        const settle = () => {
          if (settled) return;
          settled = true;
          if (inFlightLarge.get(src) === priority) inFlightLarge.delete(src);
          if (typeof options.onSettled === 'function') options.onSettled();
        };
        warm.onload = () => {
          loadedLarge.add(src);
          settle();
        };
        warm.onerror = settle;
        warm.decoding = 'async';
        if ('fetchPriority' in warm) warm.fetchPriority = priority;
        warm.src = src;
        return true;
      }

      // A card image is worth warming only when it is actually visible to the buyer:
      // its card is in the DOM and not hidden by an active filter (applyFilters sets
      // card.hidden). This is the visible-card test Codex's review asked for — it skips
      // filtered-out cards (no wasted bandwidth) AND includes filter-hydrated cards
      // inside deferred groups that lack .is-revealed (the images the buyer can open).
      // Cards that haven't been rendered yet (deferred groups before reveal, filtered-out
      // tail) have no image button in the DOM, so they aren't matched here at all.
      function isImageButtonVisible(button) {
        if (!button || !root.contains(button)) return false;
        const card = button.closest(selectors.card);
        return Boolean(card) && !card.hidden;
      }

      function nextQueuedImageButton() {
        while (backgroundPreloadQueue.length) {
          const button = backgroundPreloadQueue.shift();
          const src = button && button.getAttribute('data-sawac-image-large');
          // Re-check visibility at dequeue time so a filter applied after the queue was
          // built skips now-hidden cards instead of warming them.
          if (src && isImageButtonVisible(button) && !loadedLarge.has(src) && !inFlightLarge.has(src)) return button;
        }
        return null;
      }

      function shouldDeferBackgroundPreload() {
        return Date.now() - lastInteractionAt < 1200 || document.visibilityState !== 'visible';
      }

      function scheduleNextBackgroundPreload(delay = backgroundPreloadBetweenDelay) {
        if (!backgroundPreloadQueue.length || backgroundPreloadTimer || backgroundPreloadInFlight) return;
        backgroundPreloadTimer = window.setTimeout(() => {
          backgroundPreloadTimer = 0;
          if (shouldDeferBackgroundPreload()) {
            scheduleNextBackgroundPreload(1200);
            return;
          }

          const runWhenIdle = (callback) => {
            if ('requestIdleCallback' in window) {
              window.requestIdleCallback(callback, { timeout: 2500 });
            } else {
              window.setTimeout(() => callback({ didTimeout: true, timeRemaining: () => 0 }), 250);
            }
          };

          runWhenIdle((deadline) => {
            if (shouldDeferBackgroundPreload()) {
              scheduleNextBackgroundPreload(1200);
              return;
            }
            if (!deadline.didTimeout && deadline.timeRemaining() < 8) {
              scheduleNextBackgroundPreload(500);
              return;
            }
            const button = nextQueuedImageButton();
            if (!button) return;
            backgroundPreloadInFlight = true;
            const started = preloadLarge(button, {
              priority: 'low',
              onSettled: () => {
                backgroundPreloadInFlight = false;
                scheduleNextBackgroundPreload();
              }
            });
            if (!started) {
              backgroundPreloadInFlight = false;
              scheduleNextBackgroundPreload(0);
            }
          });
        }, delay);
      }

      function startPostLoadImageWarmup() {
        // Build the queue from the cards the buyer can actually open right now (visible,
        // filter-aware) instead of from deferred-group reveal state alone. applyFilters()
        // has already run by window.load, so a ?q= deep link or a pre-load filter is
        // reflected here — we warm matching images and skip filtered-out ones.
        const buttons = Array.from(root.querySelectorAll(selectors.openImage))
          .filter(isImageButtonVisible)
          .slice(0, backgroundPreloadLimit);
        backgroundPreloadQueue.push(...buttons);
        scheduleNextBackgroundPreload(backgroundPreloadInitialDelay);
      }

      if (document.readyState === 'complete') {
        startPostLoadImageWarmup();
      } else {
        window.addEventListener('load', startPostLoadImageWarmup, { once: true });
      }

      // The card image button carries every product image as a JSON array of 420px
      // base URLs (data-sawac-images). Fall back to the single data-sawac-image-large
      // so older/partial cards still open one slide.
      function lightboxImageList(button) {
        const raw = button.getAttribute('data-sawac-images');
        if (raw) {
          try {
            const arr = JSON.parse(raw);
            if (Array.isArray(arr) && arr.length) return arr;
          } catch (error) { /* fall back to the single large image below */ }
        }
        const large = button.getAttribute('data-sawac-image-large');
        return large ? [large] : [];
      }

      function buildLightboxDot(index) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'sawac-lightbox-dot' + (index === 0 ? ' is-active' : '');
        dot.setAttribute('data-sawac-lightbox-dot', '');
        dot.setAttribute('data-index', String(index));
        dot.setAttribute('aria-label', 'Imagen ' + (index + 1));
        return dot;
      }

      // Set a slide's full-res src on demand (active slide + neighbors), so a product
      // with many photos does not fetch every full image the moment it opens.
      function loadLightboxSlide(index) {
        const slide = lightbox.slides[index];
        if (!slide || slide.loaded) return;
        slide.loaded = true;
        slide.img.src = slide.full;
      }

      function setLightboxActive(index) {
        if (index < 0 || index >= lightbox.count) return;
        lightbox.index = index;
        if (lightbox.dots) {
          const dots = lightbox.dots.children;
          for (let i = 0; i < dots.length; i += 1) {
            dots[i].classList.toggle('is-active', i === index);
          }
        }
        loadLightboxSlide(index);
        loadLightboxSlide(index - 1);
        loadLightboxSlide(index + 1);
      }

      function updateLightboxFromScroll() {
        if (!lightbox.track) return;
        setLightboxActive(window.SawacPeek.index(lightbox.track));
      }

      function scrollLightboxTo(index) {
        if (!lightbox.track) return;
        window.SawacPeek.scrollTo(lightbox.track, index);
        setLightboxActive(index);
      }

      function openLightbox(button) {
        if (!lightbox.dialog || !lightbox.track) return;
        const bases = lightboxImageList(button);
        if (!bases.length) return;
        preloadLarge(button, { priority: 'high' });
        const alt = button.getAttribute('data-sawac-image-alt') || '';

        // Rebuild the track + dots for this product.
        lightbox.track.textContent = '';
        lightbox.dots.textContent = '';
        lightbox.slides = [];
        lightbox.count = bases.length;
        lightbox.index = 0;

        const thumb = button.querySelector('img');
        const thumbSrc = thumb && (thumb.currentSrc || thumb.src);

        bases.forEach((base, i) => {
          const slide = document.createElement('div');
          slide.className = 'sawac-lightbox-slide sawac-peek-slide';
          slide.setAttribute('data-sawac-lightbox-slide', '');

          const img = document.createElement('img');
          img.className = 'sawac-lightbox-img';
          img.alt = i === 0 ? alt : '';
          img.decoding = 'async';
          const full = imageAt(base, 1200);
          const record = { img: img, full: full, loaded: false };

          // First slide blurs up from the already-cached card thumbnail so the modal
          // is never empty; the rest load via setLightboxActive (active + neighbors).
          if (i === 0 && thumbSrc && thumbSrc !== full) {
            img.src = thumbSrc;
            img.classList.add('is-loading');
            record.loaded = true;
            const full_img = new Image();
            full_img.onload = () => {
              img.src = full;
              img.classList.remove('is-loading');
            };
            full_img.onerror = () => { img.classList.remove('is-loading'); };
            full_img.src = full;
          }

          slide.appendChild(img);
          lightbox.track.appendChild(slide);
          lightbox.slides.push(record);

          if (bases.length > 1) lightbox.dots.appendChild(buildLightboxDot(i));
        });

        lightbox.dots.hidden = bases.length <= 1;

        if (typeof lightbox.dialog.showModal === 'function') {
          lightbox.dialog.showModal();
        } else {
          lightbox.dialog.setAttribute('open', '');
        }
        // Reopen on this product's last viewed image. Scroll AFTER showing: a
        // hidden track drops the write and engines then restore a stale offset
        // onto a never-loaded slide (black screen).
        lightbox.memoryKey = bases[0];
        const start = Math.min(lightbox.memory[bases[0]] || 0, bases.length - 1);
        window.SawacPeek.scrollTo(lightbox.track, start);
        setLightboxActive(start);
        scrollLock.lock();
      }

      function closeLightbox() {
        if (!lightbox.dialog || !lightbox.dialog.open) return;
        lightbox.dialog.classList.add('is-closing');
        const finish = () => {
          if (lightbox.memoryKey) lightbox.memory[lightbox.memoryKey] = lightbox.index;
          lightbox.dialog.classList.remove('is-closing');
          lightbox.dialog.close();
          scrollLock.unlock();
          lightbox.track.textContent = '';
          lightbox.dots.textContent = '';
          lightbox.dots.hidden = true;
          lightbox.slides = [];
          lightbox.count = 0;
          lightbox.index = 0;
        };
        const onEnd = () => {
          lightbox.dialog.removeEventListener('animationend', onEnd);
          finish();
        };
        lightbox.dialog.addEventListener('animationend', onEnd);
        // Fallback if the animation does not fire (reduced motion, etc.).
        window.setTimeout(() => {
          if (lightbox.dialog.classList.contains('is-closing')) {
            lightbox.dialog.removeEventListener('animationend', onEnd);
            finish();
          }
        }, 320);
      }

      if (lightbox.dialog) {
        lightbox.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          closeLightbox();
        });
        // Backdrop-close must ignore the click a horizontal swipe synthesizes on
        // touch: record the pointer-down point and skip closing when it moved (drag).
        let downX = 0;
        let downY = 0;
        lightbox.dialog.addEventListener('pointerdown', (event) => {
          downX = event.clientX;
          downY = event.clientY;
        }, { passive: true });
        lightbox.dialog.addEventListener('click', (event) => {
          // A dot jumps to its image; a tap on empty space (track / slide padding,
          // not the image or close button) closes the viewer.
          const dot = event.target.closest('[data-sawac-lightbox-dot]');
          if (dot) {
            scrollLightboxTo(Number(dot.getAttribute('data-index')) || 0);
            return;
          }
          if (Math.hypot(event.clientX - downX, event.clientY - downY) > 10) return;
          if (!event.target.closest('.sawac-lightbox-img') && !event.target.closest(selectors.lightboxClose)) {
            closeLightbox();
          }
        });
      }

      // Preload the full-res image as soon as the buyer hovers or starts a tap on
      // a thumbnail, so it is cached by the time openLightbox() runs.
      const onImageIntent = (event) => {
        const button = event.target.closest(selectors.openImage);
        if (button && root.contains(button)) preloadLarge(button, { priority: 'high' });
      };
      root.addEventListener('pointerover', onImageIntent);
      root.addEventListener('touchstart', onImageIntent, { passive: true });

      root.addEventListener('click', (event) => {
        const filterButton = event.target.closest(selectors.chip);
        if (filterButton && root.contains(filterButton)) {
          setFilter(filterButton);
          return;
        }

        const quickButton = event.target.closest(selectors.quickAdd);
        if (quickButton && root.contains(quickButton)) {
          quickAdd(quickButton);
          return;
        }

        const tierButton = event.target.closest(selectors.tierAdd);
        if (tierButton && root.contains(tierButton)) {
          quickAddN(tierButton.dataset.variantId, Number(tierButton.dataset.tierDoz) || 0);
          return;
        }

        const minusButton = event.target.closest(selectors.qtyMinus);
        if (minusButton && root.contains(minusButton)) {
          minusOne(minusButton);
          return;
        }

        const removeButton = event.target.closest(selectors.remove);
        if (removeButton && root.contains(removeButton)) {
          clearVariant(removeButton.dataset.variantId);
          return;
        }

        const sheetCloseButton = event.target.closest(selectors.sheetClose);
        if (sheetCloseButton && root.contains(sheetCloseButton)) {
          closeSheet();
          return;
        }

        const lightboxCloseButton = event.target.closest(selectors.lightboxClose);
        if (lightboxCloseButton && root.contains(lightboxCloseButton)) {
          closeLightbox();
          return;
        }

        const openImageButton = event.target.closest(selectors.openImage);
        if (openImageButton && root.contains(openImageButton)) {
          openLightbox(openImageButton);
          return;
        }

        const openSheetButton = event.target.closest(selectors.openSheet);
        if (openSheetButton && root.contains(openSheetButton)) {
          const card = openSheetButton.closest(selectors.card);
          if (card) openSheet(card);
          return;
        }

      });

      // Debounced: every keystroke used to run the full synchronous index scan +
      // render-on-demand pass — the other main homepage INP driver while typing.
      // 150ms trailing = one pass per pause. Chip taps stay synchronous
      // (setFilter). Each keystroke also supersedes the previous query's tails
      // right away: the queued filtered-render batches (generation bump — no
      // stale below-fold work competing with typing) and the pending/in-flight
      // server search (pre-debounce, every keystroke re-armed its 180ms timer;
      // now only supersession keeps a stale query from firing mid-debounce).
      // The debounced pass re-decides and reschedules both, so the server search
      // fires ~330ms after the last keystroke as before.
      if (searchInput) {
        let searchDebounceTimer = 0;
        searchInput.addEventListener('input', () => {
          filterRenderGeneration += 1;
          supersedeServerSearch();
          window.clearTimeout(searchDebounceTimer);
          searchDebounceTimer = window.setTimeout(applyFilters, 150);
          scheduleQueryUrlWrite(searchInput.value);
        });
      }

      // Deep-linkable search: ?q=pm504 prefills the search box so a filtered
      // catalog link can be shared (e.g. over WhatsApp). The param is read once
      // on load; applyFilters() below renders the filtered list.
      if (searchInput) {
        const initialQuery = new URLSearchParams(window.location.search).get('q');
        if (initialQuery) searchInput.value = initialQuery;
      }

      // Deep-linkable category: ?categoria=<group-handle> preselects the matching chip
      // so a shared catalog link opens already filtered to that category. Read once on
      // load; the state is set here and the applyFilters() below renders it (setFilter
      // writes this param back as chips are tapped). No matching chip -> ignored.
      const initialCategory = new URLSearchParams(window.location.search).get('categoria');
      if (initialCategory && initialCategory !== 'all') {
        root.querySelectorAll(selectors.chip).forEach((chip) => {
          if (chip.dataset.sawacFilter === initialCategory) {
            state.activeFilter = initialCategory;
            state.activeHandles = parseHandles(chip.dataset.sawacHandles);
          }
        });
      }

      // React to cart changes made elsewhere (e.g. the drawer's clear button), so the
      // catalog rows and the open sheet reflect the new cart. Self-originated events are
      // skipped: our own syncThemeCart already updated state. fetchCart() reapplies any
      // pending local +/- syncs, so an in-flight local change is not clobbered by the
      // external refresh (silentEvent avoids re-broadcasting our own cart:update).
      // Exception: when the external event says the cart is now EMPTY (Vaciar carrito),
      // that is the user's latest intent — drop pending local targets instead of
      // re-adding them to the freshly cleared cart.
      document.addEventListener('cart:update', (event) => {
        const detail = event.detail || {};
        const source = detail.data && detail.data.source;
        if (source === 'saoko-catalog') return;
        const resourceCount = detail.resource ? Number(detail.resource.item_count) : NaN;
        const dataCount = detail.data ? Number(detail.data.itemCount) : NaN;
        if (resourceCount === 0 || dataCount === 0) {
          state.pendingQtySyncs.forEach((entry) => window.clearTimeout(entry.timer));
          state.pendingQtySyncs.clear();
        }
        fetchCart({ silentEvent: true }).catch(() => {});
      });

      hydrateGroupsForActiveFilter();
      applyFilters();

      // Reveal below-the-fold groups as they near the viewport, so their cards stay
      // out of the initial eager DOM. Hydration runs well ahead of visibility (600px
      // rootMargin), so the growth happens off-screen and CLS stays ~0. No
      // IntersectionObserver support -> reveal everything up front (correctness first).
      const deferredGroups = root.querySelectorAll('[data-sawac-deferred-group]');
      if (deferredGroups.length) {
        // Reserve each deferred grid the height of a full revealed row-set, measured
        // from the REAL eager (above-the-fold) cards — they're exactly what a deferred
        // group reveals. Matching the reservation to the revealed height means revealing
        // a group doesn't resize its grid, so nothing below it shifts (CLS). Falls back
        // to the CSS calc() when no eager card is measurable.
        //
        // Set PER GROUP, not once globally: data-sawac-eager-count is each group's own
        // real card count (already capped to that collection's product count server-side),
        // and a sparser group can differ a lot from a fuller one — e.g. a 1-product
        // collection next to a 3-product one. A single reserve computed off one group and
        // applied to every grid over-reserves the sparser ones, which then collapse on
        // reveal (the exact CLS this reservation exists to prevent).
        (function reserveDeferredSpace() {
          let cardH = 0;
          root
            .querySelectorAll('[data-sawac-group]:not([data-sawac-deferred-group]) [data-sawac-card]')
            .forEach((c) => { cardH = Math.max(cardH, c.getBoundingClientRect().height); });
          if (cardH <= 0) return;
          const grid = root.querySelector('[data-sawac-grid]');
          const gap = grid ? (parseFloat(getComputedStyle(grid).rowGap) || 0) : 0;
          deferredGroups.forEach((groupEl) => {
            const ppg = parseInt(groupEl.getAttribute('data-sawac-eager-count'), 10) || 1;
            const reserve = Math.ceil(cardH * ppg + gap * Math.max(0, ppg - 1));
            groupEl.style.setProperty('--sawac-deferred-reserve', reserve + 'px');
          });
        })();

        const settleReveal = (revealedAny, hydratedAny) => {
          if (!revealedAny) return;
          // Under an active filter, reconcile which of the just-revealed cards stay
          // visible for the current filter.
          if (root.classList.contains('is-filtered')) applyFilters();
          // Whenever cards were cloned in, reflect the live cart on the new rows.
          // applyFilters() only re-syncs rows IT hydrated, so sync unconditionally on
          // our own hydration — this covers the filtered scroll-reveal path too.
          if (hydratedAny) {
            syncRowQuantities(state.cart || { item_count: 0, total_price: 0, currency: 'CLP', items: [] });
            reapplyPendingQuantitySyncs();
          }
        };
        if ('IntersectionObserver' in window) {
          const groupObserver = new IntersectionObserver((entries, obs) => {
            let revealedAny = false;
            let hydratedAny = false;
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const gm = groupModelByEl.get(entry.target);
              if (gm && gm.shell) {
                revealDeferredGroupWhenReady(entry.target).then((result) => {
                  if (!result.revealedAny) return;
                  obs.unobserve(entry.target);
                  settleReveal(result.revealedAny, result.hydratedAny);
                });
                return;
              }
              if (revealDeferredGroup(entry.target)) hydratedAny = true;
              revealedAny = true;
              obs.unobserve(entry.target);
            });
            settleReveal(revealedAny, hydratedAny);
          }, { rootMargin: '600px 0px' });
          deferredGroups.forEach((group) => groupObserver.observe(group));
        } else {
          Promise.all([...deferredGroups].map((group) => revealDeferredGroupWhenReady(group))).then((results) => {
            settleReveal(
              results.some((result) => result.revealedAny),
              results.some((result) => result.hydratedAny)
            );
          });
        }
        // Keyboard / screen-reader users don't scroll, so the observer never fires for
        // them. Reveal a deferred group when focus lands inside it (e.g. tabbing onto
        // its "Más modelos" link) so its cards become reachable in tab order.
        root.addEventListener('focusin', (event) => {
          const node = event.target;
          const group = node && node.closest ? node.closest('[data-sawac-deferred-group]:not(.is-revealed)') : null;
          if (group) {
            revealDeferredGroupWhenReady(group).then((result) => {
              settleReveal(result.revealedAny, result.hydratedAny);
            });
          }
        });
      }

      // Collection (single group): reveal the rest of the index in batches as the sentinel
      // nears the viewport. Reuses ensureCardRendered + cart re-sync; pauses while filtered
      // (a filter already renders every match); renders until the sentinel clears the band.
      root.querySelectorAll('[data-sawac-load-more]').forEach((sentinel) => {
        const groupEl = sentinel.closest('[data-sawac-group]');
        const gm = groupEl ? groupModelByEl.get(groupEl) : null;
        if (!gm || !gm.progressive) { sentinel.remove(); return; }
        const revealBatch = () => {
          const from = gm.revealCount;
          gm.revealCount = Math.min(gm.revealCount + gm.previewCount, gm.entries.length);
          let hydrated = false;
          for (let i = from; i < gm.revealCount; i += 1) {
            const card = ensureCardRendered(gm, i);
            if (card) { card.hidden = false; hydrated = true; }
          }
          if (hydrated) {
            syncRowQuantities(state.cart || { item_count: 0, total_price: 0, currency: 'CLP', items: [] });
            reapplyPendingQuantitySyncs();
          }
        };
        if (!('IntersectionObserver' in window)) {
          while (gm.revealCount < gm.entries.length) revealBatch();
          sentinel.remove();
          return;
        }
        let observer;
        const rearmProgressiveObserver = () => {
          if (!sentinel.isConnected || gm.revealCount >= gm.entries.length) return;
          observer.unobserve(sentinel);
          observer.observe(sentinel);
        };
        observer = new IntersectionObserver((records) => {
          if (!records.some((r) => r.isIntersecting)) return;
          let guard = 0;
          while (
            gm.revealCount < gm.entries.length &&
            !root.classList.contains('is-filtered') &&
            sentinel.getBoundingClientRect().top < window.innerHeight + 800 &&
            guard < 200
          ) { revealBatch(); guard += 1; }
          if (gm.revealCount >= gm.entries.length) { observer.disconnect(); sentinel.remove(); }
          else window.requestAnimationFrame(rearmProgressiveObserver);
        }, { rootMargin: '800px 0px' });
        progressiveRevealRearms.push(rearmProgressiveObserver);
        observer.observe(sentinel);
      });

      fetchCart().catch(() => syncRowQuantities({ item_count: 0, total_price: 0, currency: 'CLP', items: [] }));
  };

  const initAll = () => { document.querySelectorAll('[data-sawac-root]').forEach(initCatalogRoot); };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (target && target.querySelector) initCatalogRoot(target.querySelector('[data-sawac-root]'));
  });
})();
